// =============================================================================
// BP-08-05: Vendor Portal Frontend - ProductForm Component
// =============================================================================

import React, { useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2, GripVertical, Info } from 'lucide-react';
import { productSchema, type ProductFormData, type VendorProduct } from '@/features/vendor/types/vendorTypes';
import { useProductFormData } from '@/features/vendor/hooks/useVendorProducts';
import { ImageUploader } from './ImageUploader';

interface ProductFormProps {
  product?: VendorProduct;
  onSubmit: (data: ProductFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  existingImages?: string[];
  onImageUpload?: (files: File[]) => Promise<void>;
  onImageDelete?: (imageUrl: string) => Promise<void>;
}

export function ProductForm({
  product,
  onSubmit,
  onCancel,
  isSubmitting = false,
  existingImages = [],
  onImageUpload,
  onImageDelete,
}: ProductFormProps) {
  const { categories, diagnoses, isLoading: isLoadingFormData } = useProductFormData();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: product?.name || '',
      shortDescription: product?.shortDescription || '',
      description: product?.description || '',
      categoryId: product?.categoryId || '',
      priceType: product?.priceType || 'fixed',
      priceMin: product?.priceMin || null,
      priceMax: product?.priceMax || null,
      acceptsInsurance: product?.acceptsInsurance || false,
      insuranceNotes: product?.insuranceNotes || '',
      features: product?.features || [],
      specifications: product?.specifications || [],
      diagnosisIds: product?.diagnosisIds || [],
      ageRangeMin: product?.ageRangeMin || null,
      ageRangeMax: product?.ageRangeMax || null,
      status: product?.status || 'draft',
    },
  });

  const {
    fields: featureFields,
    append: appendFeature,
    remove: removeFeature,
  } = useFieldArray({
    control,
    name: 'features' as never,
  });

  const {
    fields: specFields,
    append: appendSpec,
    remove: removeSpec,
  } = useFieldArray({
    control,
    name: 'specifications',
  });

  const priceType = watch('priceType');
  const acceptsInsurance = watch('acceptsInsurance');

  // Initialize features array if empty
  useEffect(() => {
    if (featureFields.length === 0) {
      appendFeature('');
    }
  }, []);

  const handleFormSubmit = (data: ProductFormData) => {
    // Clean up empty features
    const cleanedData = {
      ...data,
      features: data.features.filter((f) => f.trim() !== ''),
      specifications: data.specifications.filter((s) => s.key.trim() !== ''),
    };
    onSubmit(cleanedData);
  };

  if (isLoadingFormData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-terracotta border-t-transparent" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8">
      {/* Basic Information */}
      <section className="bg-white rounded-xl p-6 border border-gray-200">
        <h2 className="font-display text-lg font-semibold text-navy mb-6">
          Basic Information
        </h2>

        <div className="space-y-6">
          {/* Product Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-navy mb-2">
              Product Name *
            </label>
            <input
              id="name"
              type="text"
              {...register('name')}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors"
              placeholder="Enter product name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          {/* Short Description */}
          <div>
            <label htmlFor="shortDescription" className="block text-sm font-medium text-navy mb-2">
              Short Description
              <span className="text-warm-gray font-normal ml-1">(max 500 characters)</span>
            </label>
            <textarea
              id="shortDescription"
              {...register('shortDescription')}
              rows={2}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors resize-none"
              placeholder="Brief summary for listings"
            />
            {errors.shortDescription && (
              <p className="mt-1 text-sm text-red-500">{errors.shortDescription.message}</p>
            )}
          </div>

          {/* Full Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-navy mb-2">
              Full Description *
            </label>
            <textarea
              id="description"
              {...register('description')}
              rows={6}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors resize-none"
              placeholder="Detailed product description..."
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
            )}
          </div>

          {/* Category */}
          <div>
            <label htmlFor="categoryId" className="block text-sm font-medium text-navy mb-2">
              Category *
            </label>
            <select
              id="categoryId"
              {...register('categoryId')}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors bg-white"
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            {errors.categoryId && (
              <p className="mt-1 text-sm text-red-500">{errors.categoryId.message}</p>
            )}
          </div>
        </div>
      </section>

      {/* Images */}
      <section className="bg-white rounded-xl p-6 border border-gray-200">
        <h2 className="font-display text-lg font-semibold text-navy mb-6">
          Product Images
        </h2>
        <ImageUploader
          existingImages={existingImages}
          onUpload={onImageUpload}
          onDelete={onImageDelete}
          maxFiles={10}
        />
      </section>

      {/* Pricing */}
      <section className="bg-white rounded-xl p-6 border border-gray-200">
        <h2 className="font-display text-lg font-semibold text-navy mb-6">Pricing</h2>

        <div className="space-y-6">
          {/* Price Type */}
          <div>
            <label className="block text-sm font-medium text-navy mb-3">
              Pricing Type *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { value: 'fixed', label: 'Fixed Price' },
                { value: 'range', label: 'Price Range' },
                { value: 'quote', label: 'Request Quote' },
                { value: 'call', label: 'Call for Pricing' },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`
                    flex items-center justify-center px-4 py-3 rounded-xl border-2 cursor-pointer transition-colors
                    ${
                      priceType === option.value
                        ? 'border-terracotta bg-terracotta/5 text-terracotta'
                        : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <input
                    type="radio"
                    value={option.value}
                    {...register('priceType')}
                    className="sr-only"
                  />
                  <span className="font-medium">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Price Inputs */}
          {(priceType === 'fixed' || priceType === 'range') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="priceMin" className="block text-sm font-medium text-navy mb-2">
                  {priceType === 'fixed' ? 'Price' : 'Minimum Price'} ($)
                </label>
                <input
                  id="priceMin"
                  type="number"
                  step="0.01"
                  {...register('priceMin', { valueAsNumber: true })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors"
                  placeholder="0.00"
                />
              </div>
              {priceType === 'range' && (
                <div>
                  <label htmlFor="priceMax" className="block text-sm font-medium text-navy mb-2">
                    Maximum Price ($)
                  </label>
                  <input
                    id="priceMax"
                    type="number"
                    step="0.01"
                    {...register('priceMax', { valueAsNumber: true })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors"
                    placeholder="0.00"
                  />
                </div>
              )}
            </div>
          )}

          {/* Insurance */}
          <div className="pt-4 border-t border-gray-100">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register('acceptsInsurance')}
                className="w-5 h-5 rounded border-gray-300 text-terracotta focus:ring-terracotta"
              />
              <span className="font-medium text-navy">Accepts Insurance</span>
            </label>
            {acceptsInsurance && (
              <div className="mt-4">
                <label htmlFor="insuranceNotes" className="block text-sm font-medium text-navy mb-2">
                  Insurance Notes
                </label>
                <textarea
                  id="insuranceNotes"
                  {...register('insuranceNotes')}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors resize-none"
                  placeholder="Which insurance providers do you work with?"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-lg font-semibold text-navy">Features</h2>
          <button
            type="button"
            onClick={() => appendFeature('')}
            className="flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta-light transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Feature
          </button>
        </div>

        <div className="space-y-3">
          {featureFields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-3">
              <GripVertical className="w-5 h-5 text-gray-300 cursor-move" />
              <input
                {...register(`features.${index}` as const)}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors"
                placeholder="Enter feature..."
              />
              <button
                type="button"
                onClick={() => removeFeature(index)}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                aria-label="Remove feature"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Specifications */}
      <section className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-lg font-semibold text-navy">Specifications</h2>
          <button
            type="button"
            onClick={() => appendSpec({ key: '', value: '', unit: '' })}
            className="flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta-light transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Specification
          </button>
        </div>

        <div className="space-y-3">
          {specFields.map((field, index) => (
            <div key={field.id} className="grid grid-cols-12 gap-3 items-center">
              <div className="col-span-4">
                <input
                  {...register(`specifications.${index}.key` as const)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors"
                  placeholder="Name (e.g., Weight)"
                />
              </div>
              <div className="col-span-5">
                <input
                  {...register(`specifications.${index}.value` as const)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors"
                  placeholder="Value (e.g., 25)"
                />
              </div>
              <div className="col-span-2">
                <input
                  {...register(`specifications.${index}.unit` as const)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors"
                  placeholder="Unit (e.g., lbs)"
                />
              </div>
              <div className="col-span-1">
                <button
                  type="button"
                  onClick={() => removeSpec(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  aria-label="Remove specification"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {specFields.length === 0 && (
            <p className="text-sm text-warm-gray text-center py-4">
              No specifications added yet. Click "Add Specification" to get started.
            </p>
          )}
        </div>
      </section>

      {/* Targeting */}
      <section className="bg-white rounded-xl p-6 border border-gray-200">
        <h2 className="font-display text-lg font-semibold text-navy mb-6">
          Targeting
          <span className="text-sm font-normal text-warm-gray ml-2">
            Help families find your product
          </span>
        </h2>

        <div className="space-y-6">
          {/* Diagnoses */}
          <div>
            <label className="block text-sm font-medium text-navy mb-3">
              Relevant Diagnoses
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {diagnoses.map((diagnosis) => (
                <label
                  key={diagnosis.id}
                  className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-cream-dark transition-colors"
                >
                  <Controller
                    name="diagnosisIds"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        value={diagnosis.id}
                        checked={field.value?.includes(diagnosis.id)}
                        onChange={(e) => {
                          const currentValues = field.value || [];
                          if (e.target.checked) {
                            field.onChange([...currentValues, diagnosis.id]);
                          } else {
                            field.onChange(currentValues.filter((id) => id !== diagnosis.id));
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-terracotta focus:ring-terracotta"
                      />
                    )}
                  />
                  <span className="text-sm">{diagnosis.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Age Range */}
          <div>
            <label className="block text-sm font-medium text-navy mb-3">
              Age Range (Years)
            </label>
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div>
                <input
                  type="number"
                  {...register('ageRangeMin', { valueAsNumber: true })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors"
                  placeholder="Min age"
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <input
                  type="number"
                  {...register('ageRangeMax', { valueAsNumber: true })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta transition-colors"
                  placeholder="Max age"
                  min="0"
                  max="100"
                />
              </div>
            </div>
            <p className="text-sm text-warm-gray mt-2 flex items-center gap-1">
              <Info className="w-4 h-4" />
              Leave blank if product is suitable for all ages
            </p>
          </div>
        </div>
      </section>

      {/* Status */}
      <section className="bg-white rounded-xl p-6 border border-gray-200">
        <h2 className="font-display text-lg font-semibold text-navy mb-6">Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { value: 'draft', label: 'Draft', desc: 'Not visible to customers' },
            { value: 'active', label: 'Active', desc: 'Visible and available' },
            { value: 'out_of_stock', label: 'Out of Stock', desc: 'Visible but unavailable' },
            { value: 'discontinued', label: 'Discontinued', desc: 'No longer offered' },
          ].map((option) => (
            <label
              key={option.value}
              className={`
                p-4 rounded-xl border-2 cursor-pointer transition-colors
                ${
                  watch('status') === option.value
                    ? 'border-terracotta bg-terracotta/5'
                    : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <input
                type="radio"
                value={option.value}
                {...register('status')}
                className="sr-only"
              />
              <span className="block font-medium text-navy">{option.label}</span>
              <span className="block text-xs text-warm-gray mt-1">{option.desc}</span>
            </label>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="flex items-center justify-end gap-4 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 text-navy font-medium rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-3 bg-gradient-to-r from-terracotta to-terracotta-light text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-60"
        >
          {isSubmitting ? 'Saving...' : product ? 'Update Product' : 'Create Product'}
        </button>
      </div>
    </form>
  );
}

export default ProductForm;

